import { useState, Dispatch, SetStateAction, useMemo } from 'react';

interface UseToggle {
  isOn: boolean;
  toggle: () => void;
  turnOn: () => void;
  turnOff: () => void;
  setIsOn: Dispatch<SetStateAction<boolean>>;
}

export function useToggle(defaultState = false): UseToggle {
  const [isOn, setIsOn] = useState(defaultState);

  const actions = useMemo(
    () => ({
      toggle: () => setIsOn((pastVisibility) => !pastVisibility),
      turnOn: () => setIsOn(true),
      turnOff: () => setIsOn(false),
      setIsOn,
    }),
    [],
  );

  return useMemo(
    () => ({
      isOn,

      // this actually does not point our action.handlers
      // to a new memory location
      ...actions,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOn],
  );
}

/*
 -------- Explanation --------

  In JS if we de-structure an object, we actually
  get the reference out of the variables inside of it
  if they came from an external ref (as our actions above)

  BUT, we are creating a new obj every time the boolean is toggled,
  otherwise our component would not re-render

  Which means

  const createModal = useToggle()

  handlers will ALWAYS point to a DIFFERENT object

  HOWEVER....

  As stated above, the actual handlers will be the same, meaning:

  const { toggle } = useToggle()

  const someFn = useCallback(() => {...}, [toggle])

  // or...

  const aggregated = useToggle()

  const someFn = useCallback(() => {...}, [aggregated.toggle])

  Will both be SAFE from re-calculations

  ----> WARNING - NOT SAFE

  const aggregated = useToggle()

  const someFn = useCallback(() => {...}, [aggregated])

  This call is **NOT** safe, as a new object is generated at each state change

  --> ESLINT can sometimes say
        useCallback(() => {...}, [aggregated.toggle])
  is actually bad and that you should use 'aggregated' on the dep. array

  DO NOT do this. Disable the rule for the line

  REMEMBER - if more deps are present into the array, verify if all the refs are
  safely referenced. it can be tricky with the rule disabled.
*/
